import React, { FC, useState } from 'react';
import { BulkCreateProps } from './BulkCreateProps';
import DefaultPageLayout from '../../common/DefaultPageLayout';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Stack } from '@mui/material';
import Button from '@ingka/button';
import ArrowLeft from '@ingka/ssr-icon/paths/arrow-left';
import Text from '@ingka/text';
import CustomTabsComponent from '../../components/CustomTabsComponent';
import BulkCreateGeneralInfo from '../../components/BulkCreateGeneralInfo';
import {
  createProject,
  flattenProjects,
  mapProjectsForRequest,
  onRowChangeHandler,
  returnRequiredFields,
  returnUnflattenedMapWithFinancials,
} from './utils';
import { v4 as uuidv4 } from 'uuid';
import { useTypedSelector } from '../../store';
import {
  countriesSelector,
  getBusinessUnits,
} from '../../store/selectData/selectors';
import BulkCreateFinancials from '../../components/BulkCreateFinancials';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import BulkNewProjectButton from '../../components/BulkNewProjectButton';
import { enqueueErrorSnackbar } from '../../store/notifications/actionWrapper';
import {
  createProjectsBulk,
  setSolutions,
} from '../../store/projects/actionWrapper';
import ConfirmationModal from '../../common/ConfirmationModal';
import { emailRegex } from '../../components/ResponsibleTab/ProjectResponsibleForm/ResponsibleField/ResponsibleAutocomplete/ResponsibleAutocomplete';
import { selectResponsibleOptionsList } from '../../store/users/selectors';
import { useAnalytics } from '../../hooks/useAnalytics';
import { SiteLocationBuType } from '../../store/projects/types';
import BulkSolutionModal from '../../components/BulkSolutionModal';
import { selectSolutions } from '../../store/projects/selectors';
import { ESTABLISHMENT_TYPE } from '../../components/ProjectTypeSelect/utils';
import { selectConfidentialPermission } from '../../store/userData/selectors';

const BulkCreate: FC<BulkCreateProps> = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const bus = useTypedSelector(getBusinessUnits);
  const countries = useTypedSelector(countriesSelector);
  const usersList = useTypedSelector(selectResponsibleOptionsList);
  const storeSolutions = useTypedSelector(selectSolutions);
  const isConfidentialAllowed = useTypedSelector(selectConfidentialPermission);
  const generalInfoRef = useGridApiRef();
  const [projects, setProjects] = useState([]);
  const [finProjects, setFinProjects] = useState([]);
  const [financialRange, setFinancialRange] = useState<[number, number]>([
    0, 0,
  ]);
  const [activeTab, setActiveTab] = useState('information');
  const [columnVisibility, setColumnVisibility] = React.useState({});
  const [confiramtionModal, setConfiramtionModal] = useState(false);
  const [solutionModal, setSolutionModal] = useState(false);
  const [activeProjectId, setActiveProjectId] = useState<string | null>(null);
  const { actionStart, actionEnd } = useAnalytics('projectCreate');

  const closeSolutionModal = () => {
    setSolutionModal(false);
    setActiveProjectId(null);
    dispatch(setSolutions([]));
  };

  const onFinancialsRowChangeHandler = (
    id: string,
    field: string,
    value: string,
  ) => {
    const newFinancials = finProjects.map((project) => {
      if (project.id === id) {
        const [fy, fieldName] = field.split('-');
        if (fieldName === 'landCost') {
          return {
            ...project,
            [field]: value,
            [`${fy}-error`]: +value > +project?.[`${fy}-propertyInvestment`],
          };
        }
        if (fieldName === 'propertyInvestment') {
          return {
            ...project,
            [field]: value,
            [`${fy}-error`]: +value < +project?.[`${fy}-landCost`],
          };
        }
        return { ...project, [field]: value };
      }
      return project;
    });
    setFinProjects(newFinancials);
  };
  const generalRowChangedHandler = (
    id: string,
    field: string,
    value: string | SiteLocationBuType,
  ) => {
    onRowChangeHandler(id, field, value, projects, setProjects, bus);
  };
  const deleteHandler = (id: string) => {
    setProjects(projects.filter((p) => p?.recordId !== id));
  };
  const copyHandler = (id) => {
    const newProject = createProject(projects.find((p) => p.recordId === id));
    setProjects([
      ...projects,
      createProject({ ...newProject, recordId: uuidv4() }),
    ]);
  };
  const createProjectHandler = (type) => {
    if (projects.length === 0) actionStart();
    setProjects([
      ...projects,
      createProject({
        establishmentType: type,
        confidential: isConfidentialAllowed ? null : 'INTERNAL',
      }),
    ]);
    const projectsMap = new Set([
      ...projects.map((p) => p.establishmentType),
      type,
    ]);
    setColumnVisibility({
      confidential: isConfidentialAllowed,
      businessUnitId:
        projectsMap.has('SISAN') ||
        projectsMap.has('RELOCATE') ||
        projectsMap.has('FORMAT_TRANSFORMATION') ||
        projectsMap.has('CLOSE'),
      newUnitType: projectsMap.has('FORMAT_TRANSFORMATION'),
      solutions:
        projectsMap.has(ESTABLISHMENT_TYPE.IMPROVE) ||
        projectsMap.has(ESTABLISHMENT_TYPE.FORMAT_CHANGE),
    });
  };
  const returnToProjects = () => {
    history.push('/');
  };

  const goBackHandler = () => {
    if (projects.length) {
      setConfiramtionModal(true);
      return;
    }
    returnToProjects();
  };
  const closeConfirmationModal = () => {
    setConfiramtionModal(false);
  };
  const triggerValidation = (projects) => {
    const valid = projects.reduce((acc, curr) => {
      const reqFieldsValid = returnRequiredFields(curr.establishmentType).every(
        (field) => !!curr[field],
      );
      const investmentsValid = curr.investments.every(
        (investment) =>
          Number(investment.landCost) <= Number(investment.propertyInvestment),
      );
      const responsiblePersonValid = emailRegex.test(
        curr.countryProjectResponsible,
      );
      const solutionsValid =
        ![
          ESTABLISHMENT_TYPE.IMPROVE,
          ESTABLISHMENT_TYPE.FORMAT_CHANGE,
        ].includes(curr.establishmentType) || !!curr.solutions.length;
      return (
        acc &&
        reqFieldsValid &&
        investmentsValid &&
        responsiblePersonValid &&
        solutionsValid
      );
    }, true);
    if (!valid) {
      generalInfoRef.current.setState((prev) => {
        return {
          ...prev,
          customParams: { validationTriggered: true },
        };
      });
    }
    return valid;
  };
  const OnTabChangeHangler = (value) => {
    if (value === 'financials') {
      const flattenedProjects = flattenProjects(projects);
      setFinProjects(flattenedProjects.projects);
      setFinancialRange([flattenedProjects.minFY, flattenedProjects.maxFY]);
    } else {
      const changedProjects = returnUnflattenedMapWithFinancials(
        finProjects,
        projects,
      );
      setProjects(changedProjects);
    }
    setActiveTab(value);
  };
  const submitHandler = () => {
    let changedProjects = projects;
    if (activeTab === 'financials') {
      changedProjects = returnUnflattenedMapWithFinancials(
        finProjects,
        projects,
      );
      setProjects(changedProjects);
      setActiveTab('information');
    }
    const valid = triggerValidation(changedProjects);
    if (!valid)
      return dispatch(
        enqueueErrorSnackbar('Validation Failed, Please fill needed fields.'),
      );
    actionEnd({
      eventSubType: 'BulkCreate',
      context: { projectsCount: changedProjects.length },
    });
    dispatch(
      createProjectsBulk({
        projects: mapProjectsForRequest(changedProjects),
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        history,
      }),
    );
  };
  const addSolutionsHandler = (id) => {
    const project = projects.find((p) => p.id === id);
    dispatch(setSolutions(project?.solutions || []));
    setSolutionModal(true);
    setActiveProjectId(id);
  };

  const saveSolutionsHanlder = () => {
    const projectsCopy = [...projects];
    const projectIndex = projectsCopy.findIndex(
      (p) => p.recordId === activeProjectId,
    );
    if (projectIndex !== -1) {
      projectsCopy[projectIndex] = {
        ...projectsCopy[projectIndex],
        solutions: storeSolutions,
      };
      setProjects(projectsCopy);
    }
    closeSolutionModal();
  };

  return (
    <>
      <DefaultPageLayout
        sidebar={() => <></>}
        mainPart={() => (
          <Stack spacing={2} flexGrow={1} overflow={'auto'} p={0}>
            <Stack direction={'row'} justifyContent={'space-between'}>
              <Stack flex={1} spacing={2}>
                <div>
                  <Button
                    type="secondary"
                    data-testid={'bulk-edit'}
                    ssrIcon={ArrowLeft}
                    size={'small'}
                    onClick={goBackHandler}
                  >
                    {'Back to projects'}
                  </Button>
                </div>
                <Text textStyle="Heading.M" tagName="h3">
                  {'Bulk Create Projects'}
                </Text>
                <Text>
                  {projects?.length
                    ? 'Create several projects at the same time, covering all different project types. Simply choose the ' +
                      'project types you need and fill in the required information. Tip: You can duplicate a project with all ' +
                      'its information!'
                    : 'Create several projects at the same time, covering all different project types. Start by creating the first project.'}
                </Text>
                {!projects?.length && (
                  <BulkNewProjectButton
                    onOptionClick={createProjectHandler}
                    blue
                  />
                )}
              </Stack>

              <Stack
                flex={1}
                direction={'row'}
                spacing={2}
                alignItems={'center'}
                justifyContent={'flex-end'}
                pr={2}
              >
                {!!projects?.length && (
                  <>
                    <BulkNewProjectButton
                      onOptionClick={createProjectHandler}
                    />
                    <div>
                      <Button
                        type="emphasised"
                        size={'small'}
                        data-testid={'update-projects'}
                        onClick={() => {
                          submitHandler();
                        }}
                      >
                        {`Create ${projects.length} Projects`}
                      </Button>
                    </div>
                  </>
                )}
              </Stack>
            </Stack>
            <Stack flexGrow={1}>
              {projects.length !== 0 && (
                <>
                  <CustomTabsComponent
                    activeTab={activeTab}
                    onTabChange={(e, value) => OnTabChangeHangler(value)}
                    tabList={[
                      { value: 'information', label: 'Project Information' },
                      {
                        value: 'financials',
                        label: 'Financial Information (optional)',
                      },
                    ]}
                    tabErrors={null}
                  />
                  <div
                    role="tabpanel"
                    hidden={'financials' !== activeTab}
                    id={`tabPanel-${activeTab}`}
                    data-testid={'financials-tab'}
                    aria-labelledby={`simple-tab-${activeTab}`}
                    style={{ height: '100%', overflowX: 'auto' }}
                  >
                    <>
                      <BulkCreateFinancials
                        projects={finProjects}
                        range={financialRange}
                        onChange={onFinancialsRowChangeHandler}
                      />
                      <BulkNewProjectButton
                        onOptionClick={createProjectHandler}
                      />
                    </>
                  </div>
                  <div
                    role="tabpanel"
                    hidden={'information' !== activeTab}
                    id={`tabPanel-${activeTab}`}
                    data-testid={'information-tab'}
                    aria-labelledby={`simple-tab-${activeTab}`}
                    style={{ height: '100%', overflowX: 'auto' }}
                  >
                    <Stack spacing={2}>
                      <>
                        <BulkCreateGeneralInfo
                          projects={projects}
                          onChange={generalRowChangedHandler}
                          columnVisibility={columnVisibility}
                          copyHandler={copyHandler}
                          deleteHandler={deleteHandler}
                          BUS={bus}
                          apiRef={generalInfoRef}
                          countryList={countries}
                          userRole={''}
                          userOptions={usersList}
                          onAddSolutionClickedHandler={addSolutionsHandler}
                        />
                        <BulkNewProjectButton
                          onOptionClick={createProjectHandler}
                        />
                      </>
                    </Stack>
                  </div>
                </>
              )}
            </Stack>
          </Stack>
        )}
      />

      <ConfirmationModal
        visible={confiramtionModal}
        closeHandler={closeConfirmationModal}
        title={'You haven’t created the project yet'}
        text={
          'If you go back now, your information won’t be saved. Would you like to stay and finish, or go back anyway?'
        }
        hideFooter
      >
        <Stack direction={'row'} spacing={2} width={'100%'}>
          <Button
            type="secondary"
            onClick={returnToProjects}
            style={{ marginBottom: '0' }}
          >
            Go back
          </Button>
          <Button type="primary" onClick={closeConfirmationModal}>
            Stay and continue
          </Button>
        </Stack>
      </ConfirmationModal>
      <BulkSolutionModal
        closeHandler={closeSolutionModal}
        saveSolutionsHandler={saveSolutionsHanlder}
        visible={solutionModal}
      />
    </>
  );
};

export default BulkCreate;
